import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, CalendarClock } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";

const FEATURE_NAME = { practice: "Practice mode", progress: "Progress dashboard", groups: "Study groups", web: "Web answers", draw: "Draw it diagrams", flashcards: "Flashcards" };
const TIER_NAME = { starter: "Starter", plus: "Plus", unlimited: "Unlimited" };

// Free members can join groups, read materials and play; creating and hosting is on paid plans.
export function PaidNote({ text, testid }) {
  const navigate = useNavigate();
  return (
    <div className="rounded-2xl border border-white/10 bg-[#121725] p-5 text-sm text-neutral-400 flex flex-wrap items-center justify-between gap-3" data-testid={testid}>
      <span>{text}</span>
      <button onClick={() => navigate("/app/billing")} data-testid={`${testid}-upgrade`}
        className="text-xs px-3 py-1.5 rounded-full border border-sky-300/30 text-sky-300 hover:bg-sky-500/10 transition-colors">See plans</button>
    </div>
  );
}

// "today at 6:00 PM", "tomorrow at 2:30 AM" or "on Sat, Jun 14 at 9:10 AM", in the student's own time zone.
export function whenText(iso) {
  if (!iso) return "soon";
  const d = new Date(iso);
  const time = d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  const day = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diff = Math.round((day(d) - day(new Date())) / 86400000);
  if (diff <= 0) return `today at ${time}`;
  if (diff === 1) return `tomorrow at ${time}`;
  return `on ${d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })} at ${time}`;
}

export const fmtDate = (d) => (d ? new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "");

export function UpgradeOptions({ detail, onPick }) {
  const navigate = useNavigate();
  const { ent } = useAuth();
  const go = (path) => { onPick?.(); navigate(path); };
  return (
    <div className="space-y-3">
      <button onClick={() => go("/app/billing")} data-testid="paywall-see-plans-btn"
        className="w-full text-left p-4 rounded-xl border border-sky-300/30 bg-sky-500/10 hover:bg-sky-500/15 transition-colors">
        <p className="text-sm font-medium text-sky-200 flex items-center gap-2"><Sparkles className="h-4 w-4" /> Upgrade your plan</p>
        <p className="text-xs text-neutral-400 mt-1">Starter $5 · Plus $10 · Unlimited $20 a month, with savings when you pay yearly.</p>
      </button>
      <button onClick={() => go("/app/billing#day-passes")} data-testid="paywall-day-pass-btn"
        className="w-full text-left p-4 rounded-xl border border-white/10 hover:bg-white/5 transition-colors">
        <p className="text-sm font-medium">Get a pass</p>
        <p className="text-xs text-neutral-400 mt-1">From $2 for 2 hours of unlimited messages. No subscription.</p>
      </button>
      {detail?.code === "limit" && (
        <p className="text-xs text-neutral-500 flex items-center gap-2 px-1" data-testid="paywall-reset-note">
          <CalendarClock className="h-3.5 w-3.5" /> Or wait. Chat opens again {whenText(detail.resets_at || ent?.resets_at)}.
        </p>
      )}
    </div>
  );
}

const FEATURE_DETAIL = { code: "feature" };

export function PaywallDialog() {
  const [detail, setDetail] = useState(null);
  useEffect(() => {
    const h = (e) => setDetail(e.detail || { code: "limit" });
    window.addEventListener("paywall", h);
    return () => window.removeEventListener("paywall", h);
  }, []);
  const isFeature = detail?.code === "feature";
  const isVideo = detail?.code === "video_limit";
  const { ent } = useAuth();
  return (
    <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
      <DialogContent className="bg-[#0E121C] border-white/10 max-w-md" data-testid="paywall-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">
            {isVideo && "This video is over your plan's limit"}
            {!isVideo && (isFeature ? `${FEATURE_NAME[detail.feature] || "This"} is on ${TIER_NAME[detail.needs] || "a paid plan"}` : "Your chat access window has ended")}
          </DialogTitle>
          <DialogDescription className="text-neutral-400">
            {isVideo && detail.message}
            {!isVideo && (isFeature ? "Unlock it with a plan or a pass."
              : <span data-testid="paywall-free-replenish">Chatting is available again {whenText(detail?.resets_at || ent?.resets_at)}. A pass gives you unlimited messages right now.</span>)}
          </DialogDescription>
        </DialogHeader>
        <UpgradeOptions detail={detail} onPick={() => setDetail(null)} />
      </DialogContent>
    </Dialog>
  );
}

export function Locked({ feature, children, inline, title, text }) {
  const { ent } = useAuth();
  if (!ent) return <div className="p-10 text-neutral-500 text-sm">Loading…</div>;
  if (ent.features[feature]) return children;
  if (inline) {
    return (
      <div className="soft-card p-6 max-w-lg" data-testid={`locked-${feature}`}>
        <p className="text-xs text-sky-300 mb-2">Starter and above</p>
        <h2 className="font-display text-xl font-semibold">{title || FEATURE_NAME[feature]}</h2>
        <p className="text-sm text-neutral-400 mt-2 mb-5">{text || "Unlock it with a plan or a pass."}</p>
        <UpgradeOptions detail={FEATURE_DETAIL} />
      </div>
    );
  }
  return (
    <div className="flex-1 overflow-y-auto scroll-thin">
      <div className="max-w-md mx-auto px-6 pt-[14vh]" data-testid={`locked-${feature}`}>
        <p className="text-xs text-sky-300 mb-3">Starter and above</p>
        <h1 className="font-display text-4xl font-semibold tracking-tight">{FEATURE_NAME[feature]}</h1>
        <p className="text-neutral-400 mt-3 mb-8">This is part of the Starter plan and up. Unlock it with a plan or a pass.</p>
        <UpgradeOptions detail={FEATURE_DETAIL} />
      </div>
    </div>
  );
}
